/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Katalog — Motion Design System
 *
 * Centralized Framer Motion animation presets, timing tokens, spring
 * configurations, and reusable variants for the entire application.
 *
 * Every animation in the app should reference these presets rather than
 * using ad-hoc values. This ensures visual consistency and makes it
 * trivial to tune the overall feel of the UI from a single file.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import type { Transition, Variants, TargetAndTransition } from "framer-motion";

// ─── Duration Tokens ─────────────────────────────────────────────────────────

/** Duration tokens in seconds (Framer Motion uses seconds). */
export const duration = {
  /** 0.15s — Micro-interactions: button press, toggle, badge */
  fast: 0.15,
  /** 0.3s — Transitions: page change, panel open, modal */
  normal: 0.3,
  /** 0.5s — Reveals: scroll animations, counter count-up entrance */
  slow: 0.5,
  /** 0.8s — Hero animations, first-load effects */
  emphasis: 0.8,
  /** 2.0s — Count-up number animation */
  countUp: 2.0,
  /** 1.5s — BookSpinner loop cycle */
  spinnerLoop: 1.5,
} as const;

// ─── Spring Configurations ──────────────────────────────────────────────────

/** Pre-tuned spring physics for different interaction feels. */
export const spring = {
  /** Buttons, toggles — snappy and responsive */
  snappy: {
    type: "spring" as const,
    stiffness: 400,
    damping: 30,
  },
  /** Pages, panels — smooth and controlled */
  smooth: {
    type: "spring" as const,
    stiffness: 300,
    damping: 25,
  },
  /** Playful elements: bookmark heart, notification bell, badges */
  bouncy: {
    type: "spring" as const,
    stiffness: 200,
    damping: 15,
  },
  /** Gentle — used for subtle entrance animations */
  gentle: {
    type: "spring" as const,
    stiffness: 260,
    damping: 20,
  },
  /** Stiff — used for layout animations and quick corrections */
  stiff: {
    type: "spring" as const,
    stiffness: 500,
    damping: 30,
  },
} as const;

// ─── Easing Curves ──────────────────────────────────────────────────────────

export const easing = {
  easeOut: [0.22, 1, 0.36, 1] as [number, number, number, number],
  easeIn: [0.4, 0, 1, 1] as [number, number, number, number],
  easeInOut: [0.42, 0, 0.58, 1] as [number, number, number, number],
  emphasize: [0.16, 1, 0.3, 1] as [number, number, number, number],
} as const;

// ─── Transition Presets ─────────────────────────────────────────────────────

export const transition = {
  /** Fast tween for micro-interactions */
  fast: {
    duration: duration.fast,
    ease: easing.easeOut,
  } satisfies Transition,

  /** Normal tween for standard transitions */
  normal: {
    duration: duration.normal,
    ease: easing.easeOut,
  } satisfies Transition,

  /** Slow tween for reveals and scroll animations */
  slow: {
    duration: duration.slow,
    ease: easing.easeOut,
  } satisfies Transition,

  /** Emphasis tween for hero animations */
  emphasis: {
    duration: duration.emphasis,
    ease: easing.emphasize,
  } satisfies Transition,

  /** Quick exit — faster than entrance for snappy feel */
  exit: {
    duration: duration.fast,
    ease: easing.easeIn,
  } satisfies Transition,

  /** Spring: snappy */
  springSnappy: spring.snappy as Transition,

  /** Spring: smooth */
  springSmooth: spring.smooth as Transition,

  /** Spring: bouncy */
  springBouncy: spring.bouncy as Transition,

  /** Spring: gentle */
  springGentle: spring.gentle as Transition,

  /** Spring: stiff */
  springStiff: spring.stiff as Transition,
} as const;

// ─── Variant Presets ────────────────────────────────────────────────────────

// ── Fade ─────────────────────────────────────────────────────────────────────

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: duration.normal, ease: easing.easeOut },
  },
};

export const fadeOut: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 0,
    transition: { duration: duration.normal, ease: easing.easeIn },
  },
};

// ── Slide ────────────────────────────────────────────────────────────────────

export const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: spring.smooth,
  },
};

export const slideDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: spring.smooth,
  },
};

export const slideLeft: Variants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: spring.smooth,
  },
};

export const slideRight: Variants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: spring.smooth,
  },
};

// ── Scale ────────────────────────────────────────────────────────────────────

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: spring.smooth,
  },
};

export const scaleOut: Variants = {
  hidden: { opacity: 1, scale: 1 },
  visible: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

// ── Stagger Containers ──────────────────────────────────────────────────────

/**
 * Container that staggers the entrance of its children.
 *
 * Usage: Apply `staggerContainer` variants to a parent `motion.div`,
 * and `staggerItem` variants to each child `motion.div`.
 *
 * @example
 * ```tsx
 * <motion.div variants={staggerContainer} initial="hidden" animate="visible">
 *   {items.map(item => (
 *     <motion.div key={item.id} variants={staggerItem}>
 *       {item.content}
 *     </motion.div>
 *   ))}
 * </motion.div>
 * ```
 */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0,
    },
  },
};

/** Faster stagger for notification items, dropdown items, etc. */
export const staggerContainerFast: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0,
    },
  },
};

/** Slower stagger for cards in grids (50ms between each). */
export const staggerContainerCards: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

/** Stagger item: fades in + slides up */
export const staggerItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

/** Stagger item: fades in + slides from right (for notifications) */
export const staggerItemRight: Variants = {
  hidden: { opacity: 0, x: 10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
};

/** Stagger item with scale for card grids */
export const staggerItemCard: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

// ── Page Transitions ────────────────────────────────────────────────────────

/** Forward page navigation: slide in from right + fade */
export const pageEnter: Variants = {
  hidden: { opacity: 0, x: 20, y: 0 },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: spring.smooth,
  },
  exit: {
    opacity: 0,
    x: -10,
    y: 0,
    transition: { duration: duration.fast * 1.5, ease: easing.easeIn },
  },
};

/** Backward page navigation: slide in from left + fade */
export const pageExit: Variants = {
  hidden: { opacity: 0, x: -20, y: 0 },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: spring.smooth,
  },
  exit: {
    opacity: 0,
    x: 10,
    y: 0,
    transition: { duration: duration.fast * 1.5, ease: easing.easeIn },
  },
};

/** Default page transition: slide up + fade */
export const pageDefault: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

/** Same-level navigation: crossfade only */
export const pageCrossfade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: duration.normal, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

/** Modal/overlay route: scale up from center */
export const pageModal: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

// ── Interactive / Micro-Interaction Variants ────────────────────────────────

/**
 * Pop animation for bookmark/rating interactions.
 *
 * Animate the `animate` prop conditionally:
 * - `{ scale: [1, 1.4, 1] }` with `spring.bouncy` on toggle
 */
export const springPop = {
  /** Animate state for the pop effect */
  pop: {
    scale: [1, 1.4, 1],
    transition: {
      type: "spring" as const,
      stiffness: 200,
      damping: 15,
      duration: 0.3,
    },
  } satisfies TargetAndTransition,
  /** Resting state */
  rest: {
    scale: 1,
    transition: spring.snappy,
  } satisfies TargetAndTransition,
};

/**
 * Bell shake animation for notification bell.
 *
 * Apply as: `animate={shouldShake ? bellShake.shake : bellShake.idle}`
 */
export const bellShake = {
  idle: {
    rotate: 0,
    transition: { duration: 0.2 },
  } satisfies TargetAndTransition,
  shake: {
    rotate: [0, 20, -18, 14, -10, 0],
    transition: {
      duration: 0.6,
      ease: easing.easeInOut,
    },
  } satisfies TargetAndTransition,
};

/**
 * Progress fill animation for progress bars/rings.
 *
 * Use with SVG `strokeDashoffset` or CSS width.
 */
export const progressFill: Variants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: {
        duration: duration.emphasis,
        ease: easing.easeOut,
      },
      opacity: { duration: duration.fast },
    },
  },
};

// ── Card Animations ─────────────────────────────────────────────────────────

/** Hover state for thesis cards: subtle scale + elevation */
export const cardHover: TargetAndTransition = {
  scale: 1.02,
  y: -4,
  transition: {
    type: "spring",
    stiffness: 400,
    damping: 25,
  },
};

/** Hover state for compact cards */
export const cardHoverCompact: TargetAndTransition = {
  scale: 1.01,
  transition: {
    type: "spring",
    stiffness: 400,
    damping: 25,
  },
};

/** Tap/press state for cards and buttons */
export const tapScale: TargetAndTransition = {
  scale: 0.97,
};

// ── Star Rating Animations ──────────────────────────────────────────────────

/** Star click pop animation */
export const starClick: TargetAndTransition = {
  scale: [1, 1.3, 1],
  transition: {
    duration: 0.3,
    ease: easing.easeOut,
  },
};

/** Star hover scale */
export const starHover: TargetAndTransition = {
  scale: 1.15,
  transition: spring.snappy,
};

/** Star resting state */
export const starRest: TargetAndTransition = {
  scale: 1,
  transition: spring.snappy,
};

// ── Notification Animations ─────────────────────────────────────────────────

/** Badge entrance: scale from 0 with bouncy spring */
export const badgeEntrance: Variants = {
  initial: { scale: 0, opacity: 0 },
  animate: {
    scale: 1,
    opacity: 1,
    transition: spring.bouncy,
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

/** Badge count change: crossfade number */
export const badgeCount: Variants = {
  initial: { y: -8, opacity: 0 },
  animate: {
    y: 0,
    opacity: 1,
    transition: spring.snappy,
  },
  exit: {
    y: 8,
    opacity: 0,
    transition: { duration: 0.1 },
  },
};

/** Notification panel entrance */
export const notificationPanel: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: -4,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: spring.snappy,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -4,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

/** Toast entrance from right */
export const toastEntrance: Variants = {
  initial: { x: "100%", scale: 0.95, opacity: 0 },
  animate: {
    x: 0,
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

// ── Secure Reader Animations ────────────────────────────────────────────────

/** Reader page transition: next page (slide left) */
export const readerPageNext: Variants = {
  hidden: { opacity: 0, x: 10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

/** Reader page transition: previous page (slide right) */
export const readerPagePrev: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    x: 10,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

// ── Modal / Dialog Animations ───────────────────────────────────────────────

/** Backdrop fade */
export const backdrop: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2 },
  },
  exit: {
    opacity: 0,
    transition: { duration: duration.fast },
  },
};

/** Dialog entrance: scale + fade */
export const dialog: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
      duration: 0.25,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

// ── Form Animations ─────────────────────────────────────────────────────────

/** Multi-step form: forward step transition */
export const formStepForward: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: spring.smooth,
  },
  exit: {
    opacity: 0,
    x: -40,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

/** Multi-step form: backward step transition */
export const formStepBackward: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: spring.smooth,
  },
  exit: {
    opacity: 0,
    x: 40,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

/** Validation error shake */
export const errorShake: TargetAndTransition = {
  x: [0, -6, 6, -4, 4, 0],
  transition: {
    duration: 0.3,
    ease: easing.easeOut,
  },
};

/** Error message slide-down entrance */
export const errorMessage: Variants = {
  hidden: { opacity: 0, y: -8, height: 0 },
  visible: {
    opacity: 1,
    y: 0,
    height: "auto",
    transition: { duration: 0.2, ease: easing.easeOut },
  },
  exit: {
    opacity: 0,
    y: -4,
    height: 0,
    transition: { duration: duration.fast, ease: easing.easeIn },
  },
};

// ── Home Page Animations ────────────────────────────────────────────────────

/** Hero title word stagger container */
export const heroTitleContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
};

/** Hero title individual word */
export const heroTitleWord: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

/** Hero subtitle: fade in after title completes */
export const heroSubtitle: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2,
      type: "spring",
      stiffness: 300,
      damping: 25,
    },
  },
};

/** Hero search bar: slide up + fade after subtitle */
export const heroSearchBar: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      type: "spring",
      stiffness: 260,
      damping: 20,
    },
  },
};

/** CTA buttons: scale from 0.9 + fade, staggered */
export const heroCTA: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: spring.smooth,
  },
};

/** Floating illustration gentle sinusoidal bob (use with CSS keyframe fallback) */
export const floatingElement: TargetAndTransition = {
  y: [0, -8, 0, 8, 0],
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: "easeInOut",
  },
};

/** Counter end pulse */
export const counterPulse: TargetAndTransition = {
  scale: [1, 1.05, 1],
  transition: { duration: 0.3, ease: easing.easeOut },
};

// ── Skeleton / Loading ──────────────────────────────────────────────────────

/**
 * Skeleton → content crossfade.
 * Apply to the content element that replaces a skeleton.
 */
export const skeletonReveal: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.2, ease: easing.easeOut },
  },
};

// ── Unread Dot (Notification) ───────────────────────────────────────────────

/** Unread dot shrink to 0 on mark-as-read */
export const unreadDot: Variants = {
  visible: {
    scale: 1,
    opacity: 1,
    transition: spring.stiff,
  },
  exit: {
    scale: 0,
    opacity: 0,
    transition: { duration: 0.2, ease: easing.easeIn },
  },
};

// ─── Reduced Motion Helpers ─────────────────────────────────────────────────

/**
 * Returns a Framer Motion `Transition` that respects reduced-motion.
 *
 * When `prefersReducedMotion` is true, returns `{ duration: 0 }` which
 * causes Framer Motion to snap to the target state immediately.
 */
export function safeTransition(
  prefersReducedMotion: boolean,
  t: Transition,
): Transition {
  if (prefersReducedMotion) return { duration: 0 };
  return t;
}

/**
 * Returns variants that collapse to instant (no visual animation)
 * when the user prefers reduced motion.
 *
 * - `hidden` becomes identical to `visible` (the target state)
 * - All transitions become `{ duration: 0 }`
 */
export function safeVariants(
  prefersReducedMotion: boolean,
  variants: Variants,
): Variants {
  if (!prefersReducedMotion) return variants;

  // Build reduced variants: both states map to `visible` state with instant transition
  const visible = {
    ...(variants.visible as Record<string, unknown>),
    transition: { duration: 0 },
  };

  const result: Variants = { ...variants };
  for (const key of Object.keys(variants)) {
    result[key] = { ...visible };
  }
  return result;
}

/**
 * Returns a hover animation target that is a no-op when reduced motion
 * is preferred (cards won't scale, just show subtle shadow via CSS).
 */
export function safeHover(
  prefersReducedMotion: boolean,
  hover: TargetAndTransition,
): TargetAndTransition | undefined {
  if (prefersReducedMotion) return undefined;
  return hover;
}

/**
 * Returns a tap animation target that is a no-op when reduced motion
 * is preferred.
 */
export function safeTap(
  prefersReducedMotion: boolean,
  tap: TargetAndTransition,
): TargetAndTransition | undefined {
  if (prefersReducedMotion) return undefined;
  return tap;
}

// ─── Reduced-Motion–Aware Presets ───────────────────────────────────────────

/**
 * Get page transition variants respecting reduced motion.
 *
 * - Full motion: slide + fade
 * - Reduced motion: instant opacity crossfade
 */
export function getPageVariants(prefersReducedMotion: boolean): Variants {
  if (prefersReducedMotion) return pageCrossfade;
  return pageDefault;
}

/**
 * Get card hover animation respecting reduced motion.
 *
 * - Full motion: scale(1.02) + translateY(-4px)
 * - Reduced motion: no scale or movement (CSS handles subtle shadow)
 */
export function getCardHover(
  prefersReducedMotion: boolean,
): TargetAndTransition | undefined {
  if (prefersReducedMotion) return undefined;
  return cardHover;
}

/**
 * Get stagger container variants respecting reduced motion.
 *
 * - Full motion: stagger children
 * - Reduced motion: instant appearance (no stagger)
 */
export function getStaggerContainer(
  prefersReducedMotion: boolean,
): Variants {
  if (prefersReducedMotion) {
    return {
      hidden: {},
      visible: {
        transition: {
          staggerChildren: 0,
          delayChildren: 0,
        },
      },
    };
  }
  return staggerContainer;
}

/**
 * Get stagger item variants respecting reduced motion.
 *
 * - Full motion: slide up + fade with spring
 * - Reduced motion: instant display
 */
export function getStaggerItem(prefersReducedMotion: boolean): Variants {
  if (prefersReducedMotion) {
    return {
      hidden: { opacity: 1, y: 0 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0 },
      },
    };
  }
  return staggerItem;
}

/**
 * Get bell shake animation respecting reduced motion.
 *
 * - Full motion: rotation keyframes
 * - Reduced motion: static (no shake)
 */
export function getBellShake(
  prefersReducedMotion: boolean,
  shouldShake: boolean,
): TargetAndTransition {
  if (prefersReducedMotion) return bellShake.idle;
  return shouldShake ? bellShake.shake : bellShake.idle;
}

// ─── CSS Keyframe Animation Durations (for Tailwind classes) ────────────────

/**
 * Map of CSS animation duration custom properties.
 * These correspond to the `animation-duration` values in `animations.css`.
 * If a component needs to apply a CSS-based animation with consistent
 * timing, it should reference these values via Tailwind arbitrary values
 * or inline styles.
 */
export const cssAnimationDuration = {
  shimmer: "1.5s",
  bellShake: "0.6s",
  bookOpen: "1.5s",
  pulseSoft: "2s",
  spinSlow: "3s",
  bounceIn: "0.6s",
  scalePop: "0.35s",
} as const;

// ─── Re-export everything as default namespace ──────────────────────────────

const motion = {
  // Timing
  duration,
  spring,
  easing,
  transition,

  // Variants
  fadeIn,
  fadeOut,
  slideUp,
  slideDown,
  slideLeft,
  slideRight,
  scaleIn,
  scaleOut,

  // Stagger
  staggerContainer,
  staggerContainerFast,
  staggerContainerCards,
  staggerItem,
  staggerItemRight,
  staggerItemCard,

  // Pages
  pageEnter,
  pageExit,
  pageDefault,
  pageCrossfade,
  pageModal,

  // Interactive
  springPop,
  bellShake,
  progressFill,
  cardHover,
  cardHoverCompact,
  tapScale,
  starClick,
  starHover,
  starRest,

  // Notifications
  badgeEntrance,
  badgeCount,
  notificationPanel,
  toastEntrance,
  unreadDot,

  // Reader
  readerPageNext,
  readerPagePrev,

  // Dialog
  backdrop,
  dialog,

  // Forms
  formStepForward,
  formStepBackward,
  errorShake,
  errorMessage,

  // Home
  heroTitleContainer,
  heroTitleWord,
  heroSubtitle,
  heroSearchBar,
  heroCTA,
  floatingElement,
  counterPulse,

  // Loading
  skeletonReveal,

  // CSS
  cssAnimationDuration,

  // Helpers
  safeTransition,
  safeVariants,
  safeHover,
  safeTap,
  getPageVariants,
  getCardHover,
  getStaggerContainer,
  getStaggerItem,
  getBellShake,
} as const;

export default motion;
